import VectorTileLayer from '@planet/maps/layer/VectorTile';
import VectorTileSource from '@planet/maps/source/VectorTile';
import MVT from 'ol/format/MVT.js';
import {Circle, Fill, Stroke, Style} from 'ol/style';
import {array, func, object, oneOf, oneOfType, string} from 'prop-types';
import React, {useEffect, useMemo, useRef} from 'react';
import {GRANERO_BASE_URL} from '@app/config';
import {getDefaultFeatureStyle, getSelectedStyle} from '@app/styles/mapStyles';
import {getHashFromId} from '@app/util/granero';

const tileLoader = (tile, url, token) => {
  tile.setLoader((extent, resolution, featureProjection) => {
    fetch(url, {
      headers: {Authorization: `Bearer ${token}`},
    }).then(resp => {
      resp.arrayBuffer().then(buf => {
        tile.setFeatures(
          tile.getFormat().readFeatures(buf, {
            extent,
            featureProjection,
          })
        );
      });
    });
  });
};

const mvtFormat = new MVT();

const styleMapper = (styles, geometryType, zIndex) => {
  return styles.map(style => {
    const fill = style['fill-color']
      ? new Fill({color: style['fill-color']})
      : undefined;
    const stroke = style['stroke-width']
      ? new Stroke({
          color: style['stroke-color'],
          width: style['stroke-width'],
          lineDash: style['stroke-line-dash'],
        })
      : undefined;

    switch (geometryType) {
      case 'Point':
      case 'Multipoint':
        return new Style({
          image: new Circle({
            radius: 6,
            stroke,
            fill,
          }),
          zIndex,
        });
      default:
        return new Style({
          stroke,
          fill,
          zIndex,
        });
    }
  });
};

const CollectionLayer = ({
  collectionId,
  token,
  onClick,
  selectedFeatures = [],
  style: styleProp,
  selectedStyle: selectedStyleProp,
  dataset = 'my',
  ...props
}) => {
  const layerRef = useRef();

  const style = styleProp || getDefaultFeatureStyle();
  const selectedStyle = selectedStyleProp || getSelectedStyle();

  const tileURL = `${GRANERO_BASE_URL}/ogc/${dataset}/collections/${collectionId}/tiles/{z}/{x}/{y}`;

  const slimmedRefs = useMemo(
    () => selectedFeatures.map(f => getHashFromId(f)),
    [selectedFeatures]
  );

  useEffect(() => {
    if (!layerRef.current) {
      return;
    }

    const map = layerRef.current.get('map');
    if (!map || !onClick) {
      return;
    }

    const onMapClick = function (event) {
      layerRef.current.getFeatures(event.pixel).then(features => {
        onClick(event, features);
      });
    };
    map.on('click', onMapClick);

    return () => {
      map.un('click', onMapClick);
    };
  }, [layerRef, onClick]);

  const selectedGeometryStyle = feature => {
    const isSelected =
      slimmedRefs.indexOf(getHashFromId(feature.properties_['pl:ref'])) !== -1;

    const geometryType = feature.getGeometry().getType();
    return isSelected
      ? styleMapper(selectedStyle, geometryType, Infinity)
      : styleMapper(style, geometryType);
  };

  return (
    <VectorTileLayer ref={layerRef} style={selectedGeometryStyle} {...props}>
      <VectorTileSource
        url={tileURL}
        options={{
          format: mvtFormat,
          tileLoadFunction: (tile, url) => tileLoader(tile, url, token),
        }}
      />
    </VectorTileLayer>
  );
};

CollectionLayer.propTypes = {
  collectionId: string,
  token: string,
  onClick: func,
  selectedFeatures: array,
  style: oneOfType([array, func, object]),
  selectedStyle: oneOfType([array, func, object]),
  dataset: oneOf(['my', 'boundaries']),
};

export default CollectionLayer;
